import type { OnboardingStep, OnboardingTour } from '../types';
import { getRevenuePerformanceValues, getRevenueCategoryValues } from '../revenueValues';
import { ensureRestaurantSelected } from '../tourRestaurant';
import { isTourTableReady, hasTourTableItems, revenueTourCategories, tableSearchExercise } from '../tableExercises';
import { findOnboardingTargets } from '../findTarget';
import { useFiltersStore } from '../../../stores/appFiltersStore';

/** The category column filter on the items table writes to the filters store. */
let requestedCategory: { id: string; name: string } | undefined;
const searchExercise = tableSearchExercise('revenue', 10, 'revenue-items-search');
const categoryValues = (): Record<string, string> => requestedCategory ? { context: 'example', category: requestedCategory.name } : { context: '' };

/**
 * This page keeps its sort in component state, so `aria-sort` on the tagged column
 * headers is the only reading of it. The reading names WHICH column is sorted as well
 * as how, because re-sorting a different column in the same direction is a change the
 * direction alone cannot see. "none" covers both an unsorted table and one that has
 * not mounted, so the baseline survives a late-arriving table.
 */
export const readItemsSort = () => {
  const headers = findOnboardingTargets('revenue-sort-headers');
  const sorted = headers.findIndex(el => (el.getAttribute('aria-sort') ?? 'none') !== 'none');
  return sorted === -1 ? 'none' : `${sorted}:${headers[sorted].getAttribute('aria-sort')}`;
};

const skip = 'onboarding.skip';
const next = 'onboarding.next';

export const revenueSteps: OnboardingStep[] = [
  {
    id: 'revenue-welcome',
    route: '/revenuehistory',
    layout: 'hero',
    illustration: 'welcomeRevenue',
    titleKey: 'onboarding.revenuePage.welcome.title',
    descriptionKey: 'onboarding.revenuePage.welcome.description',
    primaryLabelKey: 'onboarding.revenuePage.welcome.cta',
    secondaryLabelKey: skip,
  },
  {
    id: 'revenue-performance',
    route: '/revenuehistory',
    target: 'page-kpis',
    layout: 'hero',
    illustration: 'performance',
    titleKey: 'onboarding.revenuePage.performance.title',
    descriptionKey: 'onboarding.revenuePage.performance.description',
    values: getRevenuePerformanceValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'below',
    separateHoles: true,
    padding: 4,
    radius: 12,
  },
  {
    id: 'revenue-top-performers',
    route: '/revenuehistory',
    target: 'revenue-top-items',
    layout: 'compact',
    illustration: 'trophy',
    titleKey: 'onboarding.revenuePage.topPerformers.title',
    descriptionKey: 'onboarding.revenuePage.topPerformers.description',
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'above',
    padding: 8,
    radius: 16,
  },
  {
    id: 'revenue-see-all',
    route: '/revenuehistory',
    target: 'revenue-see-all',
    layout: 'spotlight',
    illustration: 'explore',
    titleKey: 'onboarding.revenuePage.seeAll.title',
    descriptionKey: 'onboarding.revenuePage.seeAll.description',
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'above',
    allowTargetInteraction: true,
    highlightTarget: true,
    advanceOnTargetClick: true,
    padding: 8,
    radius: 12,
  },
  {
    id: 'revenue-full-picture',
    route: '/revenuehistory/items',
    target: 'revenue-items-page',
    layout: 'compact',
    illustration: 'fullPicture',
    titleKey: 'onboarding.revenuePage.fullPicture.title',
    descriptionKey: 'onboarding.revenuePage.fullPicture.description',
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'above',
    onEnter: () => {
      requestedCategory = undefined;
      searchExercise.reset();
      const cleanup = ensureRestaurantSelected();
      useFiltersStore.getState().setMenuCategoryFilters([]);
      window.dispatchEvent(new Event('onboarding:revenue-reset-table'));
      return cleanup;
    },
    padding: 8,
    radius: 12,
  },
  ...(['dish', 'category', 'quantity', 'price', 'total'] as const).map(column => ({
    id: `revenue-column-${column}`,
    route: '/revenuehistory/items',
    target: `revenue-column-${column}`,
    layout: 'compact' as const,
    titleKey: `onboarding.revenuePage.columns.${column}.title`,
    descriptionKey: `onboarding.revenuePage.columns.${column}.description`,
    primaryLabelKey: next,
    showArrow: true,
    placement: 'right' as const,
  })),
  {
    id: 'revenue-select-filter',
    isAvailable: () => !isTourTableReady('revenue') || revenueTourCategories().length > 0,
    route: '/revenuehistory/items',
    target: 'revenue-category-header-control',
    layout: 'compact',
    illustration: 'filteredView',
    titleKey: 'onboarding.revenuePage.selectFilter.title',
    descriptionKey: 'onboarding.revenuePage.selectFilter.description',
    onEnter: () => {
      useFiltersStore.getState().setMenuCategoryFilters([]);
      requestedCategory = undefined;
    },
    values: () => {
      if (!requestedCategory && isTourTableReady('revenue')) {
        const categories = revenueTourCategories();
        requestedCategory = categories[Math.min(2, categories.length - 1)];
      }
      return categoryValues();
    },
    advanceWhen: () => {
      const selected = useFiltersStore.getState().menuCategoryFilters;
      if (isTourTableReady('revenue') && revenueTourCategories().length === 0) return true;
      return Boolean(requestedCategory && selected.length === 1 && selected[0] === requestedCategory.id);
    },
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'above',
    allowTargetInteraction: true,
    highlightTarget: true,
    padding: 6,
    radius: 8,
  },
  {
    isAvailable: () => Boolean(requestedCategory),
    onEnter: () => { if (requestedCategory) useFiltersStore.getState().setMenuCategoryFilters([requestedCategory.id]); },
    id: 'revenue-filter-result', route: '/revenuehistory/items', target: 'revenue-items-table', layout: 'compact',
    titleKey: 'onboarding.revenuePage.filterResult.title', descriptionKey: 'onboarding.revenuePage.filterResult.description',
    values: categoryValues, primaryLabelKey: next,
  },
  {
    isAvailable: () => !isTourTableReady('revenue') || revenueTourCategories().length > 0,
    id: 'revenue-select-all', route: '/revenuehistory/items', target: 'revenue-category-header-control', layout: 'compact',
    titleKey: 'onboarding.revenuePage.selectAll.title', descriptionKey: 'onboarding.revenuePage.selectAll.description',
    primaryLabelKey: next, secondaryLabelKey: skip,
    allowTargetInteraction: true, highlightTarget: true, showArrow: true,
    advanceWhen: () => {
      const categories = revenueTourCategories();
      const selected = useFiltersStore.getState().menuCategoryFilters;
      return isTourTableReady('revenue') && categories.every(category => selected.includes(category.id));
    },
  },
  {
    isAvailable: () => !isTourTableReady('revenue') || hasTourTableItems('revenue'),
    onEnter: () => { window.dispatchEvent(new CustomEvent('onboarding:revenue-sort', { detail: 'none' })); },
    id: 'revenue-sort', route: '/revenuehistory/items', target: 'revenue-quantity-sort', layout: 'compact',
    titleKey: 'onboarding.revenuePage.sort.title', descriptionKey: 'onboarding.revenuePage.sort.description',
    primaryLabelKey: next, secondaryLabelKey: skip,
    allowTargetInteraction: true, highlightTarget: true, showArrow: true,
    advanceWhen: () => (isTourTableReady('revenue') && !hasTourTableItems('revenue')) || readItemsSort() === '0:ascending',
  },
  {
    isAvailable: () => !isTourTableReady('revenue') || hasTourTableItems('revenue'),
    onEnter: () => { window.dispatchEvent(new CustomEvent('onboarding:revenue-sort', { detail: 'asc' })); },
    id: 'revenue-sort-ascending', route: '/revenuehistory/items', target: 'revenue-quantity-sort', layout: 'compact',
    titleKey: 'onboarding.revenuePage.sortAscending.title', descriptionKey: 'onboarding.revenuePage.sortAscending.description',
    primaryLabelKey: next, secondaryLabelKey: skip,
    allowTargetInteraction: true, highlightTarget: true, showArrow: true,
    advanceWhen: () => (isTourTableReady('revenue') && !hasTourTableItems('revenue')) || readItemsSort() === '0:descending',
  },
  {
    isAvailable: () => !isTourTableReady('revenue') || hasTourTableItems('revenue'),
    onEnter: () => { window.dispatchEvent(new CustomEvent('onboarding:revenue-sort', { detail: 'desc' })); },
    id: 'revenue-sort-descending', route: '/revenuehistory/items', target: 'revenue-column-quantity', layout: 'compact',
    titleKey: 'onboarding.revenuePage.sortDescending.title', descriptionKey: 'onboarding.revenuePage.sortDescending.description',
    primaryLabelKey: next,
  },
  {
    id: 'revenue-search',
    route: '/revenuehistory/items',
    target: 'revenue-items-search',
    layout: 'compact',
    illustration: 'jumpToItem',
    titleKey: 'onboarding.revenuePage.search.title',
    descriptionKey: 'onboarding.revenuePage.search.description',
    ...searchExercise,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'above',
    allowTargetInteraction: true,
    highlightTarget: true,
    padding: 6,
    radius: 8,
  },
  {
    isAvailable: searchExercise.hasExample,
    onEnter: searchExercise.restoreSearch,
    id: 'revenue-search-result', route: '/revenuehistory/items', target: 'revenue-items-table', emphasisTarget: 'revenue-search-result',
    layout: 'compact', titleKey: 'onboarding.revenuePage.searchResult.title', descriptionKey: 'onboarding.revenuePage.searchResult.description',
    values: searchExercise.values, primaryLabelKey: next,
  },
  {
    id: 'revenue-see-category',
    onEnter: () => { window.dispatchEvent(new Event('onboarding:revenue-reset-table')); },
    route: '/revenuehistory/items',
    target: 'revenue-view-category',
    layout: 'compact',
    illustration: 'seeCategory',
    titleKey: 'onboarding.revenuePage.seeCategory.title',
    descriptionKey: 'onboarding.revenuePage.seeCategory.description',
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'above',
    allowTargetInteraction: true,
    highlightTarget: true,
    advanceOnTargetClick: true,
    padding: 6,
    radius: 8,
  },
  {
    id: 'revenue-bigger-picture',
    route: '/revenuehistory/items',
    target: 'revenue-items-table',
    layout: 'compact',
    illustration: 'fullPicture',
    titleKey: 'onboarding.revenuePage.biggerPicture.title',
    descriptionKey: 'onboarding.revenuePage.biggerPicture.description',
    values: getRevenueCategoryValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'above',
    padding: 8,
    radius: 12,
  },
  {
    id: 'revenue-complete',
    route: '/revenuehistory',
    layout: 'complete',
    illustration: 'completeRevenue',
    titleKey: 'onboarding.revenuePage.complete.title',
    descriptionKey: 'onboarding.revenuePage.complete.description',
    primaryLabelKey: 'onboarding.viewOrders',
    primaryRoute: '/orderhistory',
    primaryTour: 'orders',
    secondaryLabelKey: 'onboarding.explorePage',
    secondaryAction: 'complete',
  },
];

export const revenueTour: OnboardingTour = {
  id: 'revenue',
  paths: ['/revenuehistory'],
  steps: revenueSteps,
};
